import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { COLORS, SIZES, SPACINGS, MQS } from '../app/common/constants/styles';
import { TranslationHelpers } from '../i18n/translationHelpers';
import ExternalHTMLFooter from './ExternalHTMLFooter';
import { IClientConfig } from '../config/clientConfig';
import { AppConfig } from '../app/appState.interface';
import { REGION, WHITELIST_ROUTES } from '../app/app.constants';
import { TextHelpers } from '../utils/textHelpers';
import { RegionHelper } from '../utils/RegionHelper';
import store from '../utils/store';
import { GlobeIcon } from '../app/common/components/icons/GlobeIcon';

const FooterWrapper = styled.footer`
    padding: ${SPACINGS.base * 2}px;
    width: 100%;
    max-width: ${SIZES.appMaxWidth}px;
    margin: 0 auto;

    @media (min-width: ${MQS.mq4Min}) {
        border-top: 1px solid ${COLORS.lightGrey};
    }
`;

const FooterRow = styled.div<FooterElementsProps>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: ${(props) => (props.region === REGION.EU ? 'wrap' : 'nowrap')};
`;

const LocaleSwitch = styled.div`
    color: ${COLORS.dark1};
    font-size: 0.875rem;
    display: flex;
    margin-left: ${SPACINGS.base}px;
    flex-direction: column;
    white-space: pre;
    letter-spacing: 0.02rem;
    border: none;
    line-height: 1;
    @media (max-width: ${MQS.mq2Max}) {
        margin-left: 0;
    }
`;

const SelectWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
`;

const CopyRight = styled.div<FooterElementsProps>`
    position: relative;
    color: ${COLORS.dark1};
    font-size: 0.875rem;
    padding: 20px 0;
    margin-left: ${(props) => (props.region === REGION.EU ? 'inherit' : 'auto')};

    @media (max-width: ${(props) => (props.region === REGION.EU ? MQS.mq2Min : MQS.mq1Min)}) {
        width: 100%;
        text-align: center;
    }
`;

const PolicyLink = styled.div`
    font-size: 0.875rem;

    a {
        margin: 0px 10px;
        color: ${COLORS.grey7};
        text-decoration: none;

        :hover {
            font-weight: bold;
        }
    }
`;

interface ILanguage {
    name: string;
    value: string;
}

interface FooterElementsProps {
    region?: string;
}

export const SupportedLanguageOptions: { [name: string]: ILanguage } = {
    EN: {
        value: 'en',
        name: 'English',
    },
    EN_UK: {
        value: 'en-UK',
        name: 'English',
    },
    DE_DE: {
        value: 'de-DE',
        name: 'Deutsch',
    },
    ES_ES: {
        value: 'es-ES',
        name: 'Español',
    },
    JA: {
        value: 'ja',
        name: '日本語',
    },
    FR: {
        value: 'fr',
        name: 'French',
    },
    ZH_TW: {
        value: 'zh-TW',
        name: '台灣繁中',
    },
    ZH_CN: {
        value: 'zh-CN',
        name: '简体中文',
    },
    UK_UA: {
        value: 'uk-UA',
        name: 'Українська',
    },
    ZH_HK: {
        value: 'zh-HK',
        name: '香港繁中',
    },
    TH: {
        value: 'th',
        name: 'ภาษาไทย',
    },
    ID: {
        value: 'id',
        name: 'Bahasa Indonesia',
    },
    KO: {
        value: 'ko',
        name: '한국인',
    },
    VI: {
        value: 'vi',
        name: 'Tiếng Việt',
    },
};

interface ICountry {
    name: string;
    lang: string;
}

function generateLanguagesMap(languageOptions: string): ICountry[] {
    const footerLanguages =
        languageOptions ?? store.getState().app.appConfig.DEFAULT_SUPPORTED_LANG;
    return footerLanguages
        ? footerLanguages
              .toUpperCase()
              .split(',')
              .filter((language) => SupportedLanguageOptions.hasOwnProperty(language))
              .map((language) => ({
                  name: SupportedLanguageOptions[language].name,
                  lang: SupportedLanguageOptions[language].value,
              }))
        : [];
}

interface IFooterProps {
    localeSet: string;
    clientConfig: IClientConfig;
    setAndSaveLocale: (currentLanguage: string, selectedLanguage: string) => void;
    changeCmpLanguage: (lang: string) => void;
    loggedIn: boolean;
    appConfig: AppConfig;
    disabledAgreementReq: boolean;
}

interface IFooterState {
    currentLanguage: string;
    privacyPolicyUrl: any;
}

export default class Footer extends React.Component<IFooterProps, IFooterState> {
    countries: ICountry[];
    selectRef: React.MutableRefObject<HTMLSelectElement>;
    selectPlaceholderRef: React.MutableRefObject<HTMLSpanElement>;
    constructor(props: IFooterProps) {
        super(props);

        this.state = {
            currentLanguage: this.props.localeSet,
            privacyPolicyUrl: '',
        };

        this.onLocaleChange = this.onLocaleChange.bind(this);
        this.updatePrivacyPolicyUrl = this.updatePrivacyPolicyUrl.bind(this);

        this.getExternalHTMLFooterUrl = this.getExternalHTMLFooterUrl.bind(this);

        this.countries = generateLanguagesMap(this.props.clientConfig.languageOptions);
        this.selectRef =
            React.createRef<HTMLSelectElement>() as React.MutableRefObject<HTMLSelectElement>;
        this.selectPlaceholderRef =
            React.createRef<HTMLSelectElement>() as React.MutableRefObject<HTMLSpanElement>;
    }

    onLocaleChange(selectedItem: string) {
        const selectedCountry: ICountry | undefined = this.countries.find(
            (item) => item.name === selectedItem,
        );
        if (selectedCountry) {
            document.documentElement.lang = selectedCountry.lang;
            const { disabledAgreementReq = false } = this.props;
            this.props.setAndSaveLocale(this.state.currentLanguage, selectedCountry.lang);
            if (this.props.loggedIn && !disabledAgreementReq) {
                this.props.changeCmpLanguage(selectedCountry.lang);
            }
            this.setState({
                currentLanguage: selectedCountry.lang,
            });
            this.updatePrivacyPolicyUrl(selectedCountry.lang);
        }
    }

    updatePrivacyPolicyUrl = (currentLanguage: string) => {
        const urls = this.props.appConfig.PRIVACY_POLICY_URLS;
        type PrivacyPolicyObjKey = keyof typeof urls;
        const localeSet = currentLanguage as PrivacyPolicyObjKey;
        this.setState({
            privacyPolicyUrl: this.props.appConfig.PRIVACY_POLICY_URLS[localeSet],
        });
    };

    getExternalHTMLFooterUrl() {
        const footerUrl = this.props.clientConfig.externalHTMLFooter;
        const { localeSet } = this.props;

        if (footerUrl !== undefined) {
            return TextHelpers.replaceLang(footerUrl, localeSet);
        }

        return undefined;
    }

    adjustSelectWidth = () => {
        const select = this.selectRef.current;
        const selectPlaceholder = this.selectPlaceholderRef.current;
        if (select && selectPlaceholder) {
            const selectedOptionText = select?.options[select.selectedIndex].text;
            selectPlaceholder.innerText = selectedOptionText;
            select.style.width = `${selectPlaceholder.offsetWidth + 30}px`;
        }
    };

    componentDidMount() {
        this.adjustSelectWidth();
        if (RegionHelper.isEU()) {
            this.updatePrivacyPolicyUrl(this.props.localeSet ?? SupportedLanguageOptions.EN.value);
        }
    }

    render() {
        if (this.countries.length === 0) {
            this.countries = generateLanguagesMap(this.props.clientConfig.languageOptions);
        }

        const defaultSelectedCountry: ICountry | undefined = this.countries.find(
            (country) => country.lang === this.props.localeSet,
        );

        const externalHTMLFooterUrl = this.getExternalHTMLFooterUrl();

        return (
            <FooterWrapper>
                <FooterRow region={RegionHelper.getRegion()}>
                    {this.props.clientConfig.languageSelectorEnabled ||
                    WHITELIST_ROUTES.includes(window.location.pathname) ? (
                        <LocaleSwitch data-qa-id="locale-switch">
                            <SelectWrapper>
                                <GlobeIcon />
                                <select
                                    onChange={(event) => {
                                        this.onLocaleChange(event.target.value);
                                        this.adjustSelectWidth();
                                    }}
                                    ref={this.selectRef}
                                    style={{
                                        letterSpacing: '0px',
                                        color: 'rgba(0, 0, 0, 0.6)',
                                        background: `${COLORS.ultralightGrey}`,
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        border: 'none',
                                        marginLeft: '5px',
                                        height: '2em',
                                    }}
                                    value={
                                        defaultSelectedCountry !== undefined
                                            ? defaultSelectedCountry.name
                                            : SupportedLanguageOptions.EN.name
                                    }
                                    data-qa-id="locale-switch-dropdown-button"
                                    aria-label={this.state.currentLanguage}
                                >
                                    {this.countries.map((country, index) => (
                                        <option
                                            key={country.name}
                                            value={country.name}
                                            data-qa-id={`locale-switch-dropdown-item-${index}`}
                                        >
                                            {country.name}
                                        </option>
                                    ))}
                                </select>
                                <span
                                    ref={this.selectPlaceholderRef}
                                    style={{
                                        position: 'absolute',
                                        top: '0',
                                        left: '-9999px',
                                    }}
                                    aria-hidden="true"
                                />
                            </SelectWrapper>
                        </LocaleSwitch>
                    ) : null}
                    {RegionHelper.isEU() && (
                        <PolicyLink>
                            <a
                                href={this.state.privacyPolicyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {TranslationHelpers.getFormattedMessage('privacy.policy')}
                            </a>
                            <Link
                                to={'/cookie-policy'}
                                target={
                                    !WHITELIST_ROUTES.includes(window.location.pathname)
                                        ? '_black'
                                        : '_self'
                                }
                            >
                                {TranslationHelpers.getFormattedMessage('cookie.policy')}
                            </Link>
                            <Link
                                to={'/points-terms-of-use'}
                                target={
                                    !WHITELIST_ROUTES.includes(window.location.pathname)
                                        ? '_black'
                                        : '_self'
                                }
                            >
                                {TranslationHelpers.getFormattedMessage('pointsTerms.policy')}
                            </Link>
                        </PolicyLink>
                    )}
                    <CopyRight className={'notranslate'} region={RegionHelper.getRegion()}>
                        {TranslationHelpers.getFormattedMessage('copyright')}
                    </CopyRight>
                </FooterRow>
                <ExternalHTMLFooter externalHTMLFooter={externalHTMLFooterUrl} />
            </FooterWrapper>
        );
    }
}
